/** @jsxImportSource @emotion/react */
import { buildLayerMetadataFromDetail } from '@app/domains/layers/utils/layerMetadataDefaults'
import { userAtom } from '@app/hooks/useUserProfile'
import { UserRole } from '@app/types/userTypes'
import { SuitabilityBadge } from '@app/domains/suitability/components/SuitabilityBadge'
import { SuitabilityGrade } from '@app/domains/suitability/types'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import { useNavigate, useOutletContext } from 'react-router-dom'
import CalloutBlock from '../../../../components/ui/CalloutBlock'
import { DataLayerDetailOutletContext } from '../types'
import {
  containerStyles,
  descriptionBoxStyles,
  iconSmallStyles,
  labelCellStyles,
  limitationsBoxStyles,
  limitationsContentStyles,
  manageButtonBoxStyles,
  paperStyles,
  sourceLinkStyles,
  sourcesListStyles,
  valueCellStyles,
} from './DataLayerDetailOverview.styles'
import { lazy } from 'react'
const UiMarkdown = lazy(() => import('@app/components/Atoms/UiMarkdown/UiMarkdown'))

export type MetadataRowProps = {
  label: string
  children: React.ReactNode
}

const MetadataRow = ({ label, children }: MetadataRowProps) => (
  <TableRow>
    <TableCell css={labelCellStyles}>
      <Typography variant="body2">{label}</Typography>
    </TableCell>
    <TableCell css={valueCellStyles}>{children}</TableCell>
  </TableRow>
)

// ── Main component ───────────────────────────────────────────────────────────

const DataLayerDetailOverview = () => {
  const { data: user } = useAtomValue(userAtom)
  const isAdmin = user?.user_roles.includes(UserRole.ADMIN)
  const navigate = useNavigate()

  const { selectedLayerDetail, layer, layerMeta } = useOutletContext<DataLayerDetailOutletContext>()

  const scenarioDetail = selectedLayerDetail?.layerDetail?.[0] ?? null
  const meta =
    layerMeta?.metadata ??
    (layer?.type !== 'Hazard' ? buildLayerMetadataFromDetail(scenarioDetail) : null)

  // Confidence: prefer meta.confidence_rating (richer), fall back to scenarioDetail.confidence
  const confidenceRating = meta?.confidence_rating ?? null
  const ownerName = meta?.owner_name ?? null
  const ownerOrg = meta?.owner_organization ?? null
  const ownerEmail = meta?.owner_email ?? null
  const provider = meta?.provider ?? null
  const date = meta?.created_at ?? null

  // Sources: merge scenarioDetail.sources + meta.source_links, dedupe by url

  const metaSources = meta?.source_links ?? []

  const hasMetadata = confidenceRating || ownerName || provider || date || metaSources.length > 0

  return (
    <Box css={containerStyles}>
      {isAdmin && (
        <Box css={manageButtonBoxStyles}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('metadata', { state: { metaSources } })}
          >
            Manage metadata
          </Button>
        </Box>
      )}
      {(meta?.description_md ?? scenarioDetail?.summary) && (
        <Box css={descriptionBoxStyles}>
          <UiMarkdown>{meta?.description_md ?? scenarioDetail?.summary ?? ''}</UiMarkdown>
        </Box>
      )}
      {/* ── Metadata table ── */}
      {hasMetadata && (
        <Paper variant="elevation" css={paperStyles}>
          <Table size="small">
            <TableBody>
              {confidenceRating && (
                <MetadataRow label="Confidence rating">
                  <SuitabilityBadge
                    letterGrade={confidenceRating as SuitabilityGrade}
                    size="sm"
                    label={confidenceRating}
                  />
                </MetadataRow>
              )}

              {(ownerName || ownerOrg) && (
                <MetadataRow label="Owner / data contact">
                  <Typography variant="body2">
                    {ownerName}
                    {ownerOrg && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}
                        — {ownerOrg}
                      </Typography>
                    )}
                  </Typography>
                  {ownerEmail && (
                    <Link href={`mailto:${ownerEmail}`} variant="body2" underline="hover">
                      {ownerEmail}
                    </Link>
                  )}
                </MetadataRow>
              )}

              {provider && (
                <MetadataRow label="Provider">
                  <Typography variant="body2">{provider}</Typography>
                </MetadataRow>
              )}

              {date && (
                <MetadataRow label="Date">
                  <Typography variant="body2">{date}</Typography>
                </MetadataRow>
              )}

              {metaSources.length > 0 && (
                <MetadataRow label="Sources">
                  <Box css={sourcesListStyles}>
                    {metaSources.map((source) => (
                      <Link
                        key={source.url}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        underline="hover"
                        css={sourceLinkStyles}
                      >
                        {source.title}
                        <OpenInNewIcon css={iconSmallStyles} />
                      </Link>
                    ))}
                  </Box>
                </MetadataRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
      {/* ── Limitations / caveats── */}
      {meta?.limitations_caveats_md && (
        <CalloutBlock title="Limitations & Caveats" variant="info">
          <UiMarkdown>{meta.limitations_caveats_md}</UiMarkdown>
        </CalloutBlock>
      )}
      {/* ── Additional notes  ── */}
      {meta?.additional_notes_md && (
        <Box css={limitationsBoxStyles}>
          <Typography variant="subtitle2">Additional notes</Typography>
          <Box css={limitationsContentStyles}>
            <UiMarkdown>{meta.additional_notes_md}</UiMarkdown>
          </Box>
        </Box>
      )}
      {/* TODO: Add initial assessment indications table */}
    </Box>
  )
}

export default DataLayerDetailOverview
